import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { NbDialogService } from '@nebular/theme';
import {
  EquipmentCountingMetricsService,
  EquipmentCountingJournalMetrics,
  EquipmentCountingJournalMetricsFilter,
  EquipmentCountingJournalSlicerValues,
  DispositionCount,
} from './equipment-counting-metrics.service';
import {
  IEquipmentCountingFilter,
  CustomDateRange,
} from '../equipment-counting-slicers/equipment-counting-slicers.component';
import { WindowService } from '../../../../@core/services/window.service';
import { MetricDetailLinesComponent } from '../metric-detail-lines/metric-detail-lines.component';
import { LostEquipmentDetailsModalComponent } from '../lost-equipment-details-modal/lost-equipment-details-modal.component';
import { NotFoundEquipmentDetailsModalComponent } from '../not-found-equipment-details-modal/not-found-equipment-details-modal.component';

@Component({
    selector: 'stack-mono-equipment-counting-metrics',
    templateUrl: './equipment-counting-metrics.component.html',
    styleUrls: ['./equipment-counting-metrics.component.scss'],
    standalone: false
})
export class EquipmentCountingMetricsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Metrics data
  metrics: EquipmentCountingJournalMetrics | null = null;
  isLoadingMetrics = false;

  // Disposition chart data
  dispositionData: DispositionCount[] = [];
  isLoadingDispositionData = false;

  // Slicer data
  filters: IEquipmentCountingFilter[] = [];
  showClearFilter = false;
  resetFilters = false;

  // Current filter state - default to exclude Cancelled journals
  currentFilter: EquipmentCountingJournalMetricsFilter = {
    excludeJournalStatus: ['Cancelled'],
  };

  // Worker counting analytics
  workerCountingSortBy = 'total_count';

  // Date range filter for counting metrics - default to Rolling 12
  countingDateRange = 'rolling_12';

  constructor(
    private metricsService: EquipmentCountingMetricsService,
    private windowService: WindowService,
    private dialogService: NbDialogService,
  ) {}

  ngOnInit(): void {
    this.initializeFilters();
    // Apply default Rolling 12 date range on init
    this.applyRolling12DateRange();
    // Load slicer values with the current filter (including date range)
    this.loadSlicerValues(this.currentFilter);
    this.loadMetrics();
    this.loadDispositionData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFilters(): void {
    this.filters = [
      {
        label: 'Status',
        field: 'journalStatus',
        selectedValues: [],
        values: [],
        isLoading: true,
      },
      {
        label: 'Location',
        field: 'locationNum',
        selectedValues: [],
        values: [],
        isLoading: true,
      },
      {
        label: 'Journal',
        field: 'journalId',
        selectedValues: [],
        values: [],
        isLoading: true,
      },
      {
        label: 'Journal Type',
        field: 'journalType',
        selectedValues: [],
        values: [],
        isLoading: true,
      },
      {
        label: 'Fleet Type',
        field: 'fleetType',
        selectedValues: [],
        values: [],
        isLoading: true,
      },
      {
        label: 'Equipment Category',
        field: 'equipmentCategory',
        selectedValues: [],
        values: [],
        isLoading: true,
      },
    ];
  }

  private loadSlicerValues(
    filter?: EquipmentCountingJournalMetricsFilter,
  ): void {
    // Set loading state for dynamic filters
    this.filters.forEach((f) => {
      f.isLoading = true;
    });

    this.metricsService
      .getSlicerValues(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const data: EquipmentCountingJournalSlicerValues =
            result.data?.getEquipmentCountingJournalSlicerValues;
          if (data) {
            // Update status filter values
            const statusFilter = this.filters.find(
              (f) => f.field === 'journalStatus',
            );
            if (statusFilter && data.statuses) {
              statusFilter.values = data.statuses.map((s) => ({
                value: s.value,
                label: `${s.value} (${s.count})`,
                checked: statusFilter.selectedValues.includes(s.value),
              }));
              statusFilter.isLoading = false;
            } else if (statusFilter) {
              statusFilter.isLoading = false;
            }

            // Update location filter values
            const locationFilter = this.filters.find(
              (f) => f.field === 'locationNum',
            );
            if (locationFilter) {
              locationFilter.values = data.locations.map((l) => ({
                value: l.value,
                label: `${l.value} (${l.count})`,
                checked: locationFilter.selectedValues.includes(l.value),
              }));
              locationFilter.isLoading = false;
            }

            // Update journal filter values
            const journalFilter = this.filters.find(
              (f) => f.field === 'journalId',
            );
            if (journalFilter) {
              journalFilter.values = data.journalIds.map((j) => ({
                value: j.value,
                label: j.label || j.value,
                checked: journalFilter.selectedValues.includes(j.value),
              }));
              journalFilter.isLoading = false;
            }

            // Update journal type filter values
            const typeFilter = this.filters.find(
              (f) => f.field === 'journalType',
            );
            if (typeFilter) {
              typeFilter.values = data.journalTypes.map((t) => ({
                value: t.value,
                label: `${t.value} (${t.count})`,
                checked: typeFilter.selectedValues.includes(t.value),
              }));
              typeFilter.isLoading = false;
            }

            // Update fleet type filter values
            const fleetTypeFilter = this.filters.find(
              (f) => f.field === 'fleetType',
            );
            if (fleetTypeFilter && data.fleetTypes) {
              fleetTypeFilter.values = data.fleetTypes.map((t: any) => ({
                value: t.value,
                label: `${t.value} (${t.count})`,
                checked: fleetTypeFilter.selectedValues.includes(t.value),
              }));
              fleetTypeFilter.isLoading = false;
            } else if (fleetTypeFilter) {
              fleetTypeFilter.isLoading = false;
            }

            // Update equipment category filter values
            const categoryFilter = this.filters.find(
              (f) => f.field === 'equipmentCategory',
            );
            if (categoryFilter && data.equipmentCategories) {
              categoryFilter.values = data.equipmentCategories.map(
                (c: any) => ({
                  value: c.value,
                  label: `${c.value} (${c.count})`,
                  checked: categoryFilter.selectedValues.includes(c.value),
                }),
              );
              categoryFilter.isLoading = false;
            } else if (categoryFilter) {
              categoryFilter.isLoading = false;
            }
          }
        },
        error: (error) => {
          console.error('Error loading slicer values:', error);
          this.filters.forEach((f) => (f.isLoading = false));
        },
      });
  }

  private loadMetrics(): void {
    this.isLoadingMetrics = true;
    this.metricsService
      .getMetrics(this.currentFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.metrics = result.data?.getEquipmentCountingJournalMetrics;
          this.isLoadingMetrics = false;
        },
        error: (error) => {
          console.error('Error loading metrics:', error);
          this.isLoadingMetrics = false;
        },
      });
  }

  private loadDispositionData(): void {
    this.isLoadingDispositionData = true;
    this.metricsService
      .getLineCountsByDisposition(this.currentFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const rawData =
            result.data?.getEquipmentCountingJournalLineCountsByDisposition ||
            [];
          // Filter out "Not Set" and blank/empty disposition codes
          this.dispositionData = rawData.filter(
            (item: DispositionCount) =>
              item.disposition &&
              item.disposition.trim() !== '' &&
              item.disposition !== 'Not Set',
          );
          this.isLoadingDispositionData = false;
        },
        error: (error) => {
          console.error('Error loading disposition data:', error);
          this.isLoadingDispositionData = false;
        },
      });
  }

  // Get total count of all disposition lines for percentage calculation
  getTotalDispositionCount(): number {
    return this.dispositionData.reduce((sum, item) => sum + item.count, 0);
  }

  // Get the maximum count for scaling the progress bars
  getMaxDispositionCount(): number {
    if (this.dispositionData.length === 0) return 0;
    return Math.max(...this.dispositionData.map((d) => d.count));
  }

  // Get the percentage of a disposition relative to total
  getDispositionPercentage(count: number): number {
    const total = this.getTotalDispositionCount();
    return total > 0 ? (count / total) * 100 : 0;
  }

  // Get the bar width relative to the maximum count
  getDispositionBarWidth(count: number): string {
    const max = this.getMaxDispositionCount();
    if (max === 0) return '0%';
    return `${(count / max) * 100}%`;
  }

  // Get color for disposition based on index
  getDispositionColor(index: number): string {
    return '#42A5F5';
  }

  /**
   * Get the relative date filter value based on the current date range selection
   * @returns The relative date expression (e.g., 'Today()', 'ThisWeek', 'Today(-365)') or undefined
   */
  private getRelativeDateFilter(): string | undefined {
    switch (this.countingDateRange) {
      case 'today':
        return 'Today()';
      case 'this_week':
        return 'ThisWeek()';
      case 'last_week':
        return 'ThisWeek(-1)';
      case 'this_month':
        return 'ThisMonth';
      case 'last_month':
        return 'ThisMonth(-1)';
      case 'this_year':
        return 'ThisYear';
      case 'last_year':
        return 'ThisYear(-1)';
      case 'rolling_12':
        return '>Today(-365)';
      case 'all_time':
        return undefined;
      case 'custom_range':
        // For custom range, return the date range in UTC ISO format
        if (this.currentFilter.startDate && this.currentFilter.endDate) {
          return `${this.currentFilter.startDate}..${this.currentFilter.endDate}`;
        }
        return undefined;
      default:
        return undefined;
    }
  }

  /**
   * Add journal status filter for drill-down windows.
   * Uses the journalStatus slicer selection if set, otherwise excludes Cancelled.
   * @param filters - The filter array to push into
   * @param columnPrefix - 'journal.' for line-level queries, '' for journal-level queries
   */
  private addJournalStatusFilter(
    filters: { column: string; value: string }[],
    columnPrefix: string,
  ): void {
    if (this.currentFilter.journalStatus?.length) {
      this.currentFilter.journalStatus.forEach((s) => {
        filters.push({ column: `${columnPrefix}status`, value: s });
      });
    } else {
      filters.push({
        column: `${columnPrefix}status`,
        value: '!Cancelled',
      });
    }
  }

  showDispositionDetail(disposition: string): void {
    // Build filter for the window service
    const filters: { column: string; value: string }[] = [];

    // Add current filters
    if (this.currentFilter.locationNum?.length) {
      this.currentFilter.locationNum.forEach((loc) => {
        filters.push({ column: 'journal.locationNum', value: loc });
      });
    }
    if (this.currentFilter.journalId?.length) {
      this.currentFilter.journalId.forEach((id) => {
        filters.push({ column: 'journalId', value: id });
      });
    }
    if (this.currentFilter.journalType?.length) {
      this.currentFilter.journalType.forEach((type) => {
        filters.push({ column: 'journal.type', value: type });
      });
    }
    // Add fleet type filter if specified
    if (this.currentFilter.fleetType?.length) {
      this.currentFilter.fleetType.forEach((ft) => {
        filters.push({ column: 'equipmentFleetType', value: ft });
      });
    }
    // Add equipment category filter if specified
    if (this.currentFilter.equipmentCategory?.length) {
      this.currentFilter.equipmentCategory.forEach((cat) => {
        filters.push({ column: 'equipment.category', value: cat });
      });
    }

    // Add date range filter using relative date expression
    const relativeDateFilter = this.getRelativeDateFilter();
    if (relativeDateFilter) {
      filters.push({ column: 'createdAt', value: relativeDateFilter });
    }

    // Apply journal status filter from slicer or default exclude Cancelled
    this.addJournalStatusFilter(filters, 'journal.');

    // Add disposition filter
    if (disposition === 'Not Set') {
      filters.push({ column: 'disposition', value: '' });
    } else {
      filters.push({ column: 'disposition', value: disposition });
    }

    this.windowService.openWindow(MetricDetailLinesComponent, {
      context: {
        object: 'EquipmentCountingJournalLine',
        filters: filters,
        detailType: 'journalLine',
        page: disposition,
      },
      objectId: `DispositionLines-${disposition}`,
      options: {
        title: `Equipment Counting Lines - ${disposition}`,
      },
    });
  }

  /**
   * Show journals filtered by status
   * @param status - Journal status to filter by (null for all journals, excludes Cancelled)
   */
  showJournalsByStatus(status: string | null): void {
    const filters: { column: string; value: string }[] = [];

    // Add current filters
    if (this.currentFilter.locationNum?.length) {
      this.currentFilter.locationNum.forEach((loc) => {
        filters.push({ column: 'locationNum', value: loc });
      });
    }
    if (this.currentFilter.journalId?.length) {
      this.currentFilter.journalId.forEach((id) => {
        filters.push({ column: 'id', value: id });
      });
    }
    if (this.currentFilter.journalType?.length) {
      this.currentFilter.journalType.forEach((type) => {
        filters.push({ column: 'type', value: type });
      });
    }

    // Add date range filter using relative date expression
    const relativeDateFilter = this.getRelativeDateFilter();
    if (relativeDateFilter) {
      filters.push({ column: 'createdAt', value: relativeDateFilter });
    }

    // Add status filter if provided (specific status drill-down)
    if (status) {
      filters.push({ column: 'status', value: status });
    } else {
      // Apply journal status filter from slicer or default exclude Cancelled
      this.addJournalStatusFilter(filters, '');
    }

    const statusLabel = status || 'All';
    this.windowService.openWindow(MetricDetailLinesComponent, {
      context: {
        object: 'EquipmentCountingJournal',
        filters: filters,
        detailType: 'journal',
      },
      objectId: `Journals-${statusLabel}`,
      options: {
        title: `Equipment Counting Journals - ${statusLabel}`,
      },
    });
  }

  /**
   * Show journal lines filtered by status
   * @param status - Line status to filter by (null for all lines)
   */
  showLinesByStatus(status: string | null): void {
    const filters: { column: string; value: string }[] = [];

    // Add current filters
    if (this.currentFilter.locationNum?.length) {
      this.currentFilter.locationNum.forEach((loc) => {
        filters.push({ column: 'journal.locationNum', value: loc });
      });
    }
    if (this.currentFilter.journalId?.length) {
      this.currentFilter.journalId.forEach((id) => {
        filters.push({ column: 'journalId', value: id });
      });
    }
    if (this.currentFilter.journalType?.length) {
      this.currentFilter.journalType.forEach((type) => {
        filters.push({ column: 'journal.type', value: type });
      });
    }
    // Add fleet type filter if specified
    if (this.currentFilter.fleetType?.length) {
      this.currentFilter.fleetType.forEach((ft) => {
        filters.push({ column: 'equipmentFleetType', value: ft });
      });
    }
    // Add equipment category filter if specified
    if (this.currentFilter.equipmentCategory?.length) {
      this.currentFilter.equipmentCategory.forEach((cat) => {
        filters.push({ column: 'equipment.category', value: cat });
      });
    }

    // Add date range filter using relative date expression
    const relativeDateFilter = this.getRelativeDateFilter();
    if (relativeDateFilter) {
      filters.push({ column: 'createdAt', value: relativeDateFilter });
    }

    // Apply journal status filter from slicer or default exclude Cancelled
    this.addJournalStatusFilter(filters, 'journal.');

    // Add status filter if provided
    if (status) {
      filters.push({ column: 'status', value: status });
    }

    const statusLabel = status || 'All';
    const context: Record<string, unknown> = {
      object: 'EquipmentCountingJournalLine',
      filters: filters,
      detailType: 'journalLine',
    };

    // Add "More Details" button for Not Found status
    if (status === 'Not Found') {
      context['showMoreDetailsButton'] = true;
      context['moreDetailsHandler'] = () => this.showNotFoundEquipmentDetails();
    }

    this.windowService.openWindow(MetricDetailLinesComponent, {
      context,
      objectId: `Lines-${statusLabel}`,
      options: {
        title: `Equipment Counting Lines - ${statusLabel}`,
      },
    });
  }

  /**
   * Show journal lines counted by a specific worker
   * @param countedBy - Worker ID who counted the lines
   * @param countedByName - Worker name for display
   */
  showLinesByWorker(countedBy: string, countedByName: string): void {
    const filters: { column: string; value: string }[] = [];

    // Add current filters
    if (this.currentFilter.locationNum?.length) {
      this.currentFilter.locationNum.forEach((loc) => {
        filters.push({ column: 'journal.locationNum', value: loc });
      });
    }
    if (this.currentFilter.journalId?.length) {
      this.currentFilter.journalId.forEach((id) => {
        filters.push({ column: 'journalId', value: id });
      });
    }
    if (this.currentFilter.journalType?.length) {
      this.currentFilter.journalType.forEach((type) => {
        filters.push({ column: 'journal.type', value: type });
      });
    }
    // Add fleet type filter if specified
    if (this.currentFilter.fleetType?.length) {
      this.currentFilter.fleetType.forEach((ft) => {
        filters.push({ column: 'equipmentFleetType', value: ft });
      });
    }
    // Add equipment category filter if specified
    if (this.currentFilter.equipmentCategory?.length) {
      this.currentFilter.equipmentCategory.forEach((cat) => {
        filters.push({ column: 'equipment.category', value: cat });
      });
    }

    // Add date range filter using relative date expression
    const relativeDateFilter = this.getRelativeDateFilter();
    if (relativeDateFilter) {
      filters.push({ column: 'createdAt', value: relativeDateFilter });
    }

    // Apply journal status filter from slicer or default exclude Cancelled
    this.addJournalStatusFilter(filters, 'journal.');

    // Add worker filter
    filters.push({ column: 'countedBy', value: countedBy });

    this.windowService.openWindow(MetricDetailLinesComponent, {
      context: {
        object: 'EquipmentCountingJournalLine',
        filters: filters,
        detailType: 'journalLine',
      },
      objectId: `WorkerLines-${countedBy}`,
      options: {
        title: `Lines Counted by ${countedByName || countedBy}`,
      },
    });
  }

  onSlicerChange(event: {
    filter: IEquipmentCountingFilter;
    selectedValues: any[];
  }): void {
    const { filter, selectedValues } = event;

    // Extract actual values from slicer objects (they have value, label, checked properties)
    const extractedValues = selectedValues.map((item) =>
      typeof item === 'object' && item !== null ? item.value : item,
    );

    // Update the filter's selected values
    filter.selectedValues = extractedValues;

    // Update the current filter based on the field
    if (filter.field === 'journalStatus') {
      this.currentFilter.journalStatus =
        extractedValues.length > 0 ? extractedValues : undefined;
      // When status slicer is used, remove the excludeJournalStatus default
      // so user's explicit selection takes precedence
      if (extractedValues.length > 0) {
        this.currentFilter.excludeJournalStatus = undefined;
      } else {
        // Restore the default exclude when no status is selected
        this.currentFilter.excludeJournalStatus = ['Cancelled'];
      }
    } else if (filter.field === 'locationNum') {
      this.currentFilter.locationNum =
        extractedValues.length > 0 ? extractedValues : undefined;
    } else if (filter.field === 'journalId') {
      this.currentFilter.journalId =
        extractedValues.length > 0 ? extractedValues : undefined;
    } else if (filter.field === 'journalType') {
      this.currentFilter.journalType =
        extractedValues.length > 0 ? extractedValues : undefined;
    } else if (filter.field === 'fleetType') {
      this.currentFilter.fleetType =
        extractedValues.length > 0 ? extractedValues : undefined;
    } else if (filter.field === 'equipmentCategory') {
      this.currentFilter.equipmentCategory =
        extractedValues.length > 0 ? extractedValues : undefined;
    }

    // Create a new object reference to trigger Angular change detection
    // This ensures child components like worker-counting-analytics detect the change
    this.currentFilter = { ...this.currentFilter };

    // Update showClearFilter
    this.showClearFilter = this.filters.some(
      (f) => f.selectedValues.length > 0,
    );

    // Reload metrics and disposition data with new filter
    this.loadMetrics();
    this.loadDispositionData();
  }

  onSlicerClose(_showLoading: boolean): void {
    // Reload slicer values with current filter to enable cascading filters
    // This updates the available options in other slicers based on current selection
    // Moved here from onSlicerChange to avoid showing loading spinner while slicer is open
    this.loadSlicerValues(this.currentFilter);
  }

  clearFilters(): void {
    this.resetFilters = true;
    // Create a new object with default excludeJournalStatus to trigger Angular change detection
    // This ensures child components like worker-counting-analytics detect the change
    this.currentFilter = {
      excludeJournalStatus: ['Cancelled'],
    };
    // Re-apply the default date range filter
    this.applyRolling12DateRange();
    // Create new reference after applying date range
    this.currentFilter = { ...this.currentFilter };
    this.filters.forEach((f) => (f.selectedValues = []));
    this.showClearFilter = false;

    // Reset the flag after a tick
    setTimeout(() => {
      this.resetFilters = false;
    }, 100);

    // Reload slicer values without filters
    this.loadSlicerValues();

    // Reload metrics and disposition data without filters
    this.loadMetrics();
    this.loadDispositionData();
  }

  // Helper methods for template
  getCompletionPercentage(): number {
    return this.metrics?.completionRate || 0;
  }

  getVariancePercentage(): number {
    return this.metrics?.varianceRate || 0;
  }

  getJournalStatusColor(status: string): string {
    const colors: Record<string, string> = {
      Completed: '#2dd36f', // Green
      InProgress: '#0e4aff', // Blue
      Ready: '#222b45', // Dark/Black
      Recount: '#ffa726', // Yellow/Orange
      Cancelled: '#ff3d71', // Red
    };
    return '#0e4aff'; //colors[status] || '#222b45';
  }

  getLineStatusColor(status: string): string {
    const colors: Record<string, string> = {
      Counted: '#2dd36f', // Green
      Waiting_Count: '#ffa726', // Yellow/Orange
      Not_Found: '#ff3d71', // Red
      Added: '#92949c', // Gray
      Other_Location: '#2dd36f', // Green
    };
    return colors[status] || '#222b45';
  }

  onWorkerCountingSortChange(sortBy: string): void {
    this.workerCountingSortBy = sortBy;
  }

  /**
   * Refresh all data - metrics, disposition data, and slicer values
   */
  refreshData(): void {
    this.loadSlicerValues(this.currentFilter);
    this.loadMetrics();
    this.loadDispositionData();
  }

  /**
   * Apply Rolling 12 months date range
   */
  private applyRolling12DateRange(): void {
    const now = new Date();
    const startDate = new Date(now);
    startDate.setMonth(startDate.getMonth() - 12);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);

    this.currentFilter.startDate = startDate.toISOString();
    this.currentFilter.endDate = endDate.toISOString();
  }

  /**
   * Handle date range change for counting metrics
   * @param dateRange - The selected date range value
   */
  onCountingDateRangeChange(dateRange: string): void {
    this.countingDateRange = dateRange;

    // Calculate date range based on selection
    const now = new Date();
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    switch (dateRange) {
      case 'rolling_12': {
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 12);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
        );
        break;
      case 'this_week': {
        const dayOfWeek = now.getDay();
        startDate = new Date(now);
        startDate.setDate(now.getDate() - dayOfWeek);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case 'last_week': {
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59, 999);
        startDate = lastWeekStart;
        endDate = lastWeekEnd;
        break;
      }
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'this_year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'last_year':
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
        break;
      case 'custom_range':
        // Don't update dates here - wait for custom date selection
        return;
      case 'all_time':
      default:
        startDate = undefined;
        endDate = undefined;
        break;
    }

    // Update the current filter with date range
    // Create a new object reference to trigger Angular change detection
    // This ensures child components like worker-counting-analytics detect the change
    if (startDate && endDate) {
      this.currentFilter = {
        ...this.currentFilter,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    } else {
      this.currentFilter = {
        ...this.currentFilter,
        startDate: undefined,
        endDate: undefined,
      };
    }

    // Reload slicer values with new date filter to update available options
    this.loadSlicerValues(this.currentFilter);

    // Reload data with new date filter
    this.loadMetrics();
    this.loadDispositionData();
  }

  /**
   * Handle custom date range change
   * @param customRange - The custom date range with start and end dates
   */
  onCustomDateRangeChange(customRange: CustomDateRange): void {
    if (customRange.startDate && customRange.endDate) {
      const startDate = new Date(customRange.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(customRange.endDate);
      endDate.setHours(23, 59, 59, 999);

      // Create a new object reference to trigger Angular change detection
      // This ensures child components like worker-counting-analytics detect the change
      this.currentFilter = {
        ...this.currentFilter,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };

      // Reload slicer values with new date filter to update available options
      this.loadSlicerValues(this.currentFilter);

      // Reload data with new date filter
      this.loadMetrics();
      this.loadDispositionData();
    }
  }

  /**
   * Format large numbers for display (e.g., 10000 -> 10k)
   * @param value - The number to format
   * @returns Formatted string
   */
  formatMetricValue(value: number): string {
    if (value >= 1000) {
      const kValue = value / 1000;
      // Use one decimal place for values like 7.5K, but no decimal for round numbers like 21K
      if (kValue % 1 === 0) {
        return Math.round(kValue) + 'K';
      }
      return kValue.toFixed(1) + 'K';
    }
    return value.toLocaleString();
  }

  /**
   * Format currency values for display
   * @param value - The currency value to format
   * @returns Formatted currency string
   */
  formatCurrency(value: number): string {
    if (value >= 1000000) {
      return '$' + (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return '$' + Math.round(value / 1000) + 'K';
    }
    return '$' + value.toLocaleString();
  }

  /**
   * Format age in years for display
   * @param value - The age value in years
   * @returns Formatted age string
   */
  formatAge(value: number): string {
    if (value === 0) return '0YR';
    return Math.round(value) + 'YR';
  }

  /**
   * Get accuracy percentage
   * % Accuracy = Counted (found location = journal location) / Original Count (journal location)
   * Excludes lines with blank journal location, Added, and Other Location
   * Note: We floor the value to avoid showing 100% when it's actually 99.x%
   */
  getAccuracyPercentage(): number {
    if (!this.metrics) return 0;
    const rate = this.metrics.accuracyRate || 0;
    // Floor the value to avoid rounding up (e.g., 99.5 should show as 99%, not 100%)
    return Math.floor(rate);
  }

  /**
   * Get accuracy color based on percentage
   */
  getAccuracyColor(): string {
    const accuracy = this.getAccuracyPercentage();
    if (accuracy >= 90) return '#2dd36f'; // Green
    if (accuracy >= 70) return '#ffa726'; // Yellow/Orange
    return '#ff3d71'; // Red
  }

  /**
   * Get accuracy dash array for SVG gauge
   */
  getAccuracyDashArray(): string {
    const accuracy = this.getAccuracyPercentage();
    // The arc length is approximately 126 (for a 180-degree arc with radius 40)
    const arcLength = 126;
    const filledLength = (accuracy / 100) * arcLength;
    return `${filledLength} ${arcLength}`;
  }

  /**
   * Get incorrect location percentage
   * Incorrect Location % = Location (false) / journal location
   * Excludes lines with blank journal location
   */
  getIncorrectLocationPercentage(): number {
    if (!this.metrics) return 0;
    return this.metrics.incorrectLocationRate || 0;
  }

  /**
   * Get incorrect station percentage
   * Incorrect Station % = Station (false) / journal station
   * Excludes lines with blank journal location
   */
  getIncorrectStationPercentage(): number {
    if (!this.metrics) return 0;
    return this.metrics.incorrectStationRate || 0;
  }

  /**
   * Get color for incorrect location percentage
   * <10% Green, 10-20% Yellow, >20% Red
   */
  getIncorrectLocationColor(): string {
    const percentage = this.getIncorrectLocationPercentage();
    if (percentage < 10) return '#2dd36f'; // Green
    if (percentage <= 20) return '#ffa726'; // Yellow/Orange
    return '#ff3d71'; // Red
  }

  /**
   * Get color for incorrect station percentage
   * <10% Green, 10-20% Yellow, >20% Red
   */
  getIncorrectStationColor(): string {
    const percentage = this.getIncorrectStationPercentage();
    if (percentage < 10) return '#2dd36f'; // Green
    if (percentage <= 20) return '#ffa726'; // Yellow/Orange
    return '#ff3d71'; // Red
  }

  /**
   * Get missing percentage
   * Missing % = Line Status 'Missing' / journal location
   */
  getMissingPercentage(): number {
    if (!this.metrics) return 0;
    return this.metrics.missingRate || 0;
  }

  /**
   * Show lines with incorrect location
   */
  showIncorrectLocationLines(): void {
    const filters: { column: string; value: string }[] = [];

    // Add current filters
    if (this.currentFilter.locationNum?.length) {
      this.currentFilter.locationNum.forEach((loc) => {
        filters.push({ column: 'journal.locationNum', value: loc });
      });
    }
    if (this.currentFilter.journalId?.length) {
      this.currentFilter.journalId.forEach((id) => {
        filters.push({ column: 'journalId', value: id });
      });
    }
    if (this.currentFilter.journalType?.length) {
      this.currentFilter.journalType.forEach((type) => {
        filters.push({ column: 'journal.type', value: type });
      });
    }
    // Add fleet type filter if specified
    if (this.currentFilter.fleetType?.length) {
      this.currentFilter.fleetType.forEach((ft) => {
        filters.push({ column: 'equipmentFleetType', value: ft });
      });
    }
    // Add equipment category filter if specified
    if (this.currentFilter.equipmentCategory?.length) {
      this.currentFilter.equipmentCategory.forEach((cat) => {
        filters.push({ column: 'equipment.category', value: cat });
      });
    }

    // Add date range filter using relative date expression
    const relativeDateFilter = this.getRelativeDateFilter();
    if (relativeDateFilter) {
      filters.push({ column: 'createdAt', value: relativeDateFilter });
    }

    // Apply journal status filter from slicer or default exclude Cancelled
    this.addJournalStatusFilter(filters, 'journal.');

    // Filter to only show Own equipment
    filters.push({ column: 'equipmentOwnerStatus', value: 'Own' });

    // Add wrong location filter
    filters.push({ column: 'wrongLocation', value: 'true' });

    this.windowService.openWindow(MetricDetailLinesComponent, {
      context: {
        object: 'EquipmentCountingJournalLine',
        filters: filters,
        detailType: 'journalLine',
      },
      objectId: 'IncorrectLocationLines',
      options: {
        title: 'Equipment Counting Lines - Incorrect Location',
      },
    });
  }

  /**
   * Show lines with incorrect station
   */
  showIncorrectStationLines(): void {
    const filters: { column: string; value: string }[] = [];

    // Add current filters
    if (this.currentFilter.locationNum?.length) {
      this.currentFilter.locationNum.forEach((loc) => {
        filters.push({ column: 'journal.locationNum', value: loc });
      });
    }
    if (this.currentFilter.journalId?.length) {
      this.currentFilter.journalId.forEach((id) => {
        filters.push({ column: 'journalId', value: id });
      });
    }
    if (this.currentFilter.journalType?.length) {
      this.currentFilter.journalType.forEach((type) => {
        filters.push({ column: 'journal.type', value: type });
      });
    }
    // Add fleet type filter if specified
    if (this.currentFilter.fleetType?.length) {
      this.currentFilter.fleetType.forEach((ft) => {
        filters.push({ column: 'equipmentFleetType', value: ft });
      });
    }
    // Add equipment category filter if specified
    if (this.currentFilter.equipmentCategory?.length) {
      this.currentFilter.equipmentCategory.forEach((cat) => {
        filters.push({ column: 'equipment.category', value: cat });
      });
    }

    // Add date range filter using relative date expression
    const relativeDateFilter = this.getRelativeDateFilter();
    if (relativeDateFilter) {
      filters.push({ column: 'createdAt', value: relativeDateFilter });
    }

    // Apply journal status filter from slicer or default exclude Cancelled
    this.addJournalStatusFilter(filters, 'journal.');

    // Filter to only show Own equipment
    filters.push({ column: 'equipmentOwnerStatus', value: 'Own' });

    // Add wrong station filter
    filters.push({ column: 'wrongStation', value: 'true' });

    this.windowService.openWindow(MetricDetailLinesComponent, {
      context: {
        object: 'EquipmentCountingJournalLine',
        filters: filters,
        detailType: 'journalLine',
      },
      objectId: 'IncorrectStationLines',
      options: {
        title: 'Equipment Counting Lines - Incorrect Station',
      },
    });
  }

  /**
   * Show missing lines (Missing status)
   */
  showMissingLines(): void {
    const filters: { column: string; value: string }[] = [];

    // Add current filters
    if (this.currentFilter.locationNum?.length) {
      this.currentFilter.locationNum.forEach((loc) => {
        filters.push({ column: 'journal.locationNum', value: loc });
      });
    }
    if (this.currentFilter.journalId?.length) {
      this.currentFilter.journalId.forEach((id) => {
        filters.push({ column: 'journalId', value: id });
      });
    }
    if (this.currentFilter.journalType?.length) {
      this.currentFilter.journalType.forEach((type) => {
        filters.push({ column: 'journal.type', value: type });
      });
    }
    // Add fleet type filter if specified
    if (this.currentFilter.fleetType?.length) {
      this.currentFilter.fleetType.forEach((ft) => {
        filters.push({ column: 'equipmentFleetType', value: ft });
      });
    }
    // Add equipment category filter if specified
    if (this.currentFilter.equipmentCategory?.length) {
      this.currentFilter.equipmentCategory.forEach((cat) => {
        filters.push({ column: 'equipment.category', value: cat });
      });
    }

    // Add date range filter using relative date expression
    const relativeDateFilter = this.getRelativeDateFilter();
    if (relativeDateFilter) {
      filters.push({ column: 'createdAt', value: relativeDateFilter });
    }

    // Apply journal status filter from slicer or default exclude Cancelled
    this.addJournalStatusFilter(filters, 'journal.');

    // Add Missing status filter
    filters.push({ column: 'status', value: 'Missing' });

    this.windowService.openWindow(MetricDetailLinesComponent, {
      context: {
        object: 'EquipmentCountingJournalLine',
        filters: filters,
        detailType: 'journalLine',
      },
      objectId: 'MissingLines',
      options: {
        title: 'Equipment Counting Lines - Missing Equipment',
      },
    });
  }

  /**
   * Show lost equipment details modal
   */
  showLostEquipmentDetails(): void {
    this.dialogService.open(LostEquipmentDetailsModalComponent, {
      context: {
        filters: this.currentFilter,
      },
    });
  }

  /**
   * Show not found equipment details modal
   */
  showNotFoundEquipmentDetails(): void {
    this.dialogService.open(NotFoundEquipmentDetailsModalComponent, {
      context: {
        filters: this.currentFilter,
      },
    });
  }
}
